/**
 * MSAL configuration for the v2 add-in's Tier 3 fallback.
 *
 * Per memory `feedback_reuse_existing_aad_apps` and plan §"Auth rewiring":
 * we reuse ora's existing AAD app id rather than registering a new one.
 * The env vars below are documented in `.env.example`:
 *
 *   VITE_AAD_TENANT_ID         (== `VITE_AAD_TENANT_ID`)
 *   VITE_AAD_PLUGIN_CLIENT_ID  (the v2 canonical name; falls back to
 *                              `VITE_AAD_CLIENT_ID` for parity with the
 *                              naming used in the plan brief)
 *
 * Tier 3 only fires when Tier 1 (Office.auth.getAccessToken) and Tier 2
 * (Office Dialog → Product API handoff in `services/authClient.ts`) both
 * fail — so this config is intentionally minimal.
 */

import { LogLevel, type Configuration } from "@azure/msal-browser";

interface AadConfig {
  tenantId: string;
  clientId: string;
  productApiAudience: string;
}

/**
 * Resolve the AAD config without crashing under ts-jest (which can't
 * parse `import.meta.env`). Vite injects the values at build time via
 * the `__OUTLOOK_ADDIN_AAD_CONFIG__` global declared in vite.config.ts;
 * tests see it as undefined and fall back to env-keyed look-ups using
 * `process.env`-style access on `globalThis`.
 */
declare const __OUTLOOK_ADDIN_AAD_CONFIG__: AadConfig | undefined;

function resolveAadConfig(): AadConfig {
  // 1) Build-time global (production / `npm run dev`).
  if (typeof __OUTLOOK_ADDIN_AAD_CONFIG__ !== "undefined" && __OUTLOOK_ADDIN_AAD_CONFIG__) {
    return __OUTLOOK_ADDIN_AAD_CONFIG__;
  }
  // 2) Runtime fallback for tests / non-vite contexts.
  const env = (globalThis as unknown as { process?: { env?: Record<string, string | undefined> } })
    .process?.env ?? {};
  const tenantId = env.VITE_AAD_TENANT_ID ?? "";
  const clientId =
    env.VITE_AAD_PLUGIN_CLIENT_ID ??
    env.VITE_AAD_CLIENT_ID ??
    "";
  const productApiAudience =
    env.VITE_AAD_PRODUCT_API_AUDIENCE ??
    (clientId ? `api://${clientId}` : "");
  return { tenantId, clientId, productApiAudience };
}

const aad = resolveAadConfig();

/**
 * Stable redirect URI for the SPA-platform redirect on ora's AAD app.
 * In Outlook the task pane is served from `https://localhost:3100` (dev)
 * or the deployed origin (test/prod). The path is `/auth/callback`
 * because MSAL.js's redirect handler only fires when the page that
 * loaded MSAL has a `code` query param — using a dedicated route
 * isolates the handler from the main task pane bundle.
 */
function resolveRedirectUri(): string {
  if (typeof window === "undefined" || !window.location?.origin) {
    return "https://localhost:3100/auth/callback";
  }
  return `${window.location.origin}/auth/callback`;
}

export const msalConfig: Configuration = {
  auth: {
    clientId: aad.clientId,
    authority: aad.tenantId
      ? `https://login.microsoftonline.com/${aad.tenantId}`
      : "https://login.microsoftonline.com/common",
    redirectUri: resolveRedirectUri(),
    postLogoutRedirectUri:
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "https://localhost:3100",
  },
  cache: {
    cacheLocation: "localStorage",
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
      piiLoggingEnabled: false,
    },
  },
};

/**
 * Login (interactive) request — basic OIDC scopes plus ora's
 * `access_as_user` scope so the silent-acquire path doesn't have to
 * prompt for incremental consent.
 */
export const loginRequest = {
  scopes: aad.clientId
    ? ["openid", "profile", "email", `api://${aad.clientId}/access_as_user`]
    : ["openid", "profile", "email"],
  prompt: "select_account" as const,
};

/**
 * API request — used by `acquireTokenSilent` / `acquireTokenPopup`.
 * `apiRequest.scopes` resolves to ora's existing `access_as_user` scope
 * — the same scope the manifest declares as the add-in's API permission.
 */
export const apiRequest = {
  scopes: aad.clientId ? [`api://${aad.clientId}/access_as_user`] : [],
};

export const aadConfig: AadConfig = aad;
